import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';

const emptyExpenseForm = {
  id: null,
  name: '',
  category: 'Monthly Expense',
  monthly_amount: '',
  priority: 1,
  due_day: '',
  auto_allocate: 1,
  is_active: 1,
  notes: '',
};

const emptyProfitForm = {
  profit_date: '',
  platform: 'Shopify',
  net_amount: '',
  notes: '',
};

const emptyInventorySpendForm = {
  id: null,
  spend_date: '',
  amount: '',
  vendor: '',
  payment_method: '',
  reference: '',
  notes: '',
};

const emptyGoalForm = {
  id: null,
  name: '',
  description: '',
  target_amount: '',
  current_amount: '',
  contribution_percent: '',
  priority: 1,
  status: 'ACTIVE',
  starts_after_goal_id: '',
};

const formatMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '$0.00';

  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0%';
  return `${num.toFixed(1)}%`;
};

const cleanNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const todayDate = () => {
  return new Date().toLocaleDateString('en-CA');
};

const currentMonth = () => {
  return new Date().toLocaleDateString('en-CA').slice(0, 7);
};

const getSafeDate = (value) => {
  if (!value) return '—';
  return String(value).slice(0, 10);
};

const getProgressColor = (percent) => {
  if (percent >= 100) return 'linear-gradient(90deg, #22c55e, #16a34a)';
  if (percent >= 75) return 'linear-gradient(90deg, #38bdf8, #2563eb)';
  if (percent >= 40) return 'linear-gradient(90deg, #f59e0b, #f97316)';
  return 'linear-gradient(90deg, #ef4444, #dc2626)';
};

const pageStyle = {
  minHeight: '100vh',
  background:
    'radial-gradient(circle at top left, rgba(59,130,246,0.20), transparent 32%), radial-gradient(circle at top right, rgba(34,197,94,0.12), transparent 28%), linear-gradient(180deg, #020617 0%, #0f172a 42%, #111827 100%)',
  color: '#e5e7eb',
};

const shellStyle = {
  maxWidth: 1800,
  margin: '0 auto',
};

const cardStyle = {
  background: 'rgba(15, 23, 42, 0.86)',
  border: '1px solid rgba(148, 163, 184, 0.16)',
  borderRadius: 22,
  color: '#e5e7eb',
  boxShadow: '0 18px 45px rgba(0,0,0,0.28)',
  backdropFilter: 'blur(10px)',
};

const cardSoftStyle = {
  background: 'rgba(30, 41, 59, 0.82)',
  border: '1px solid rgba(148, 163, 184, 0.14)',
  borderRadius: 20,
  color: '#e5e7eb',
};

const heroStyle = {
  background:
    'linear-gradient(135deg, rgba(37,99,235,0.35), rgba(15,23,42,0.94) 42%, rgba(22,163,74,0.20))',
  border: '1px solid rgba(148, 163, 184, 0.18)',
  borderRadius: 28,
  color: '#f8fafc',
  boxShadow: '0 24px 70px rgba(0,0,0,0.35)',
};

const inputStyle = {
  background: '#020617',
  color: '#e5e7eb',
  border: '1px solid rgba(148, 163, 184, 0.35)',
  borderRadius: 12,
};

const tableStyle = {
  color: '#e5e7eb',
};

const mutedStyle = {
  color: '#94a3b8',
};

const dividerStyle = {
  borderColor: 'rgba(148, 163, 184, 0.14)',
};

const pillStyle = {
  border: '1px solid rgba(148, 163, 184, 0.20)',
  background: 'rgba(15, 23, 42, 0.72)',
  color: '#cbd5e1',
  borderRadius: 999,
  padding: '6px 11px',
  fontSize: 12,
  fontWeight: 700,
};

const getMoneyColor = (value) => {
  const num = cleanNumber(value);
  if (num > 0) return '#22c55e';
  if (num < 0) return '#ef4444';
  return '#cbd5e1';
};

const getAutoAllocationTone = (autoAllocation) => {
  const allocated = cleanNumber(autoAllocation?.allocated_total);

  if (allocated > 0) {
    return {
      title: 'Auto fill applied',
      color: '#86efac',
      border: 'rgba(34,197,94,0.35)',
      background: 'rgba(34,197,94,0.12)',
      icon: '✅',
    };
  }

  return {
    title: 'Auto fill checked',
    color: '#bfdbfe',
    border: 'rgba(59,130,246,0.35)',
    background: 'rgba(59,130,246,0.12)',
    icon: '🔄',
  };
};

export default function Budget() {
  const [dashboard, setDashboard] = useState(null);
  const [goalsDashboard, setGoalsDashboard] = useState(null);
  const [recentGoalContributions, setRecentGoalContributions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth());
  const [loading, setLoading] = useState(true);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [savingExpense, setSavingExpense] = useState(false);
  const [savingProfit, setSavingProfit] = useState(false);
  const [savingSpend, setSavingSpend] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);
  const [recordingGoal, setRecordingGoal] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [error, setError] = useState('');
  const [goalsError, setGoalsError] = useState('');
  const [autoFillNotice, setAutoFillNotice] = useState(null);

  const expenseFormRef = useRef(null);
  const goalFormRef = useRef(null);

  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseForm, setExpenseForm] = useState(emptyExpenseForm);

  const [showGoalForm, setShowGoalForm] = useState(false);
  const [goalForm, setGoalForm] = useState(emptyGoalForm);
  const [customGoalContributionAmount, setCustomGoalContributionAmount] = useState('');
  const [goalContributionNotes, setGoalContributionNotes] = useState('');

  const [profitForm, setProfitForm] = useState({
    ...emptyProfitForm,
    profit_date: todayDate(),
  });

  const [inventorySpendForm, setInventorySpendForm] = useState({
    ...emptyInventorySpendForm,
    spend_date: todayDate(),
  });

  const expenses = dashboard?.expenses || [];
  const allocations = dashboard?.allocations || [];
  const dailyProfits = dashboard?.dailyProfits || [];
  const inventorySpend = dashboard?.inventorySpend || [];
  const summary = dashboard?.summary || {};
  const todayShopifyMoney = dashboard?.todayShopifyMoney || {};
  const inventorySpendingCard = dashboard?.inventorySpendingCard || {};

  const goals = goalsDashboard?.goals || [];
  const activeGoal = goalsDashboard?.activeGoal || null;
  const todayGoalNetProfit = cleanNumber(goalsDashboard?.todayNetProfit);
  const recommendedGoalContribution = cleanNumber(goalsDashboard?.recommendedContribution);
  const contributedTodayToGoals = cleanNumber(goalsDashboard?.contributedToday);

  const activeGoals = useMemo(() => {
    return goals.filter((goal) => String(goal.status || '').toUpperCase() === 'ACTIVE');
  }, [goals]);

  const completedGoals = useMemo(() => {
    return goals.filter((goal) => String(goal.status || '').toUpperCase() === 'COMPLETED');
  }, [goals]);

  const pausedGoals = useMemo(() => {
    return goals.filter((goal) => String(goal.status || '').toUpperCase() === 'PAUSED');
  }, [goals]);

  const totalGoalTarget = useMemo(() => {
    return goals.reduce((sum, goal) => sum + cleanNumber(goal.target_amount), 0);
  }, [goals]);

  const totalGoalSaved = useMemo(() => {
    return goals.reduce((sum, goal) => sum + cleanNumber(goal.current_amount), 0);
  }, [goals]);

  const totalGoalRemaining = Math.max(0, totalGoalTarget - totalGoalSaved);

  const totalGoalProgress = useMemo(() => {
    if (totalGoalTarget <= 0) return 0;
    return Math.min(100, Math.max(0, (totalGoalSaved / totalGoalTarget) * 100));
  }, [totalGoalSaved, totalGoalTarget]);

  const remainingAfterGoalRecommended = Math.max(
    0,
    todayGoalNetProfit - recommendedGoalContribution
  );

  const totalGross = cleanNumber(summary.totalBudgetGross ?? summary.gross);
  const totalSales = cleanNumber(summary.totalBudgetSales ?? totalGross);
  const totalBuyerShippingPaid = cleanNumber(summary.totalBuyerShippingPaid);
  const totalCogs = cleanNumber(summary.totalBudgetCogs ?? summary.cogs);
  const totalNet = cleanNumber(summary.totalBudgetNet ?? summary.net ?? summary.totalBudgetProfit);
  const totalPlatformFees = cleanNumber(summary.totalBudgetPlatformFees);
  const totalShippingCost = cleanNumber(summary.totalBudgetShippingCost);
  const totalStoreCreditUsed = cleanNumber(summary.totalBudgetStoreCreditUsed);
  const totalAllocatedToExpenses = cleanNumber(summary.totalAllocatedToExpenses);
  const totalInventorySpend = cleanNumber(summary.totalInventorySpend);

  const inventoryBudgetFromCogs = cleanNumber(
    summary.inventoryBudgetFromCogs ??
      inventorySpendingCard.cogsEarnedThisMonth ??
      totalCogs
  );

  const inventoryBudgetUsed = cleanNumber(
    summary.inventoryBudgetUsed ??
      inventorySpendingCard.inventorySpendThisMonth ??
      totalInventorySpend
  );

  const toSpendOnInventory = cleanNumber(
    summary.toSpendOnInventory ??
      summary.inventoryToSpend ??
      summary.inventoryBudgetRemaining ??
      inventorySpendingCard.toSpendOnInventory ??
      inventorySpendingCard.remaining ??
      inventoryBudgetFromCogs - inventoryBudgetUsed
  );

  const expensesRemaining = cleanNumber(summary.totalExpensesRemaining);
  const safeRemainingGross = cleanNumber(summary.safeRemainingGross);
  const safeRemainingNet = cleanNumber(summary.safeRemainingNet ?? summary.safeRemainingProfit);

  const todayGross = cleanNumber(
    todayShopifyMoney.gross ?? dashboard?.todayShopifyGross
  );
  const todayBuyerShippingPaid = cleanNumber(todayShopifyMoney.buyerShippingPaid);
  const todayCogs = cleanNumber(todayShopifyMoney.cogs ?? dashboard?.todayShopifyCogs);
  const todayNet = cleanNumber(todayShopifyMoney.net ?? dashboard?.todayShopifyNet ?? dashboard?.todayNetProfit);

  const expenseTarget = cleanNumber(summary.totalMonthlyExpenseTarget);
  const expensesFilled = cleanNumber(summary.totalExpensesFilled);

  const totalExpenseProgress = useMemo(() => {
    if (expenseTarget <= 0) return 0;
    return Math.min(100, Math.max(0, (expensesFilled / expenseTarget) * 100));
  }, [expenseTarget, expensesFilled]);

  const cogsPercent = useMemo(() => {
    if (totalGross <= 0) return 0;
    return (totalCogs / totalGross) * 100;
  }, [totalGross, totalCogs]);

  const expensePercentOfNet = useMemo(() => {
    if (totalNet <= 0) return 0;
    return (totalAllocatedToExpenses / totalNet) * 100;
  }, [totalNet, totalAllocatedToExpenses]);

  const inventorySpendPercentOfCogs = useMemo(() => {
    if (inventoryBudgetFromCogs <= 0) return 0;
    return (inventoryBudgetUsed / inventoryBudgetFromCogs) * 100;
  }, [inventoryBudgetFromCogs, inventoryBudgetUsed]);

  const inventoryRemainingPercent = useMemo(() => {
    if (inventoryBudgetFromCogs <= 0) return 0;
    return (toSpendOnInventory / inventoryBudgetFromCogs) * 100;
  }, [inventoryBudgetFromCogs, toSpendOnInventory]);

  const moneyStatus = useMemo(() => {
    if (expensesRemaining > 0) {
      return {
        label: 'Expenses Need Net Funding',
        tone: '#f59e0b',
        bg: 'rgba(245, 158, 11, 0.14)',
        border: 'rgba(245, 158, 11, 0.35)',
        icon: '⚠️',
        message: `${formatMoney(expensesRemaining)} still needs to be filled from net profit.`,
      };
    }

    if (recommendedGoalContribution > 0 && activeGoal) {
      return {
        label: 'Goal Funding Ready',
        tone: '#fbbf24',
        bg: 'rgba(251, 191, 36, 0.14)',
        border: 'rgba(251, 191, 36, 0.35)',
        icon: '🎯',
        message: `${formatMoney(recommendedGoalContribution)} can be put toward "${activeGoal.name}" based on its put-back rule.`,
      };
    }

    if (toSpendOnInventory > 0) {
      return {
        label: 'Inventory Money Available',
        tone: '#22c55e',
        bg: 'rgba(34, 197, 94, 0.14)',
        border: 'rgba(34, 197, 94, 0.35)',
        icon: '✅',
        message: `${formatMoney(toSpendOnInventory)} is available to spend on inventory from monthly COGS.`,
      };
    }

    return {
      label: 'Budget Balanced',
      tone: '#94a3b8',
      bg: 'rgba(148, 163, 184, 0.12)',
      border: 'rgba(148, 163, 184, 0.25)',
      icon: '📊',
      message: 'Monthly expenses are covered, inventory COGS money is tracked, and goals are ready when net is available.',
    };
  }, [expensesRemaining, toSpendOnInventory, recommendedGoalContribution, activeGoal]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await api.get('/budget/dashboard', {
        params: {
          month: selectedMonth,
        },
      });

      setDashboard(res.data || null);
    } catch (err) {
      console.error('[Budget] dashboard error:', err.response?.data || err.message);

      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to load budget dashboard.'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadGoalsDashboard = async () => {
    try {
      setGoalsLoading(true);
      setGoalsError('');

      const res = await api.get('/goals/dashboard');
      setGoalsDashboard(res.data || null);
    } catch (err) {
      console.error('[Budget Goals] dashboard error:', err.response?.data || err.message);

      setGoalsError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to load goals dashboard.'
      );
    } finally {
      setGoalsLoading(false);
    }
  };

  const loadRecentGoalContributions = async () => {
    try {
      const res = await api.get('/goals/contributions/recent');
      setRecentGoalContributions(res.data?.contributions || []);
    } catch (err) {
      console.error('[Budget Goals] contributions error:', err.response?.data || err.message);
    }
  };

  const loadEverything = async () => {
    await Promise.all([
      loadDashboard(),
      loadGoalsDashboard(),
      loadRecentGoalContributions(),
    ]);
  };

  useEffect(() => {
    loadDashboard();
    loadGoalsDashboard();
    loadRecentGoalContributions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  const updateExpenseForm = (key, value) => {
    setExpenseForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateProfitForm = (key, value) => {
    setProfitForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateInventorySpendForm = (key, value) => {
    setInventorySpendForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateGoalForm = (key, value) => {
    setGoalForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetExpenseForm = () => {
    setExpenseForm(emptyExpenseForm);
    setShowExpenseForm(false);
  };

  const resetGoalForm = () => {
    setGoalForm(emptyGoalForm);
    setShowGoalForm(false);
  };

  const resetInventorySpendForm = () => {
    setInventorySpendForm({
      ...emptyInventorySpendForm,
      spend_date: todayDate(),
    });
  };

  const startCreateExpense = () => {
    setExpenseForm({
      ...emptyExpenseForm,
      priority: expenses.length + 1,
    });

    setShowExpenseForm(true);

    setTimeout(() => {
      if (expenseFormRef.current) {
        expenseFormRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  const startCreateGoal = () => {
    setGoalForm({
      ...emptyGoalForm,
      priority: goals.length + 1,
      status: 'ACTIVE',
    });

    setShowGoalForm(true);

    setTimeout(() => {
      if (goalFormRef.current) {
        goalFormRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  const startEditExpense = (expense) => {
    setExpenseForm({
      id: expense.id,
      name: expense.name || '',
      category: expense.category || 'Monthly Expense',
      monthly_amount: expense.monthly_amount ?? '',
      priority: expense.priority ?? 1,
      due_day: expense.due_day || '',
      auto_allocate: Number(expense.auto_allocate ?? 1),
      is_active: Number(expense.is_active ?? 1),
      notes: expense.notes || '',
    });

    setShowExpenseForm(true);

    setTimeout(() => {
      if (expenseFormRef.current) {
        expenseFormRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  const startEditGoal = (goal) => {
    setGoalForm({
      id: goal.id,
      name: goal.name || '',
      description: goal.description || '',
      target_amount: goal.target_amount ?? '',
      current_amount: goal.current_amount ?? '',
      contribution_percent: goal.contribution_percent ?? '',
      priority: goal.priority ?? 1,
      status: goal.status || 'ACTIVE',
      starts_after_goal_id: goal.starts_after_goal_id || '',
    });

    setShowGoalForm(true);

    setTimeout(() => {
      if (goalFormRef.current) {
        goalFormRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  const startEditInventorySpend = (item) => {
    setInventorySpendForm({
      id: item.id,
      spend_date: getSafeDate(item.spend_date) || todayDate(),
      amount: item.amount ?? '',
      vendor: item.vendor || '',
      payment_method: item.payment_method || '',
      reference: item.reference || '',
      notes: item.notes || '',
    });

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const saveExpense = async () => {
    if (!expenseForm.name.trim()) {
      alert('Expense name is required.');
      return;
    }

    if (cleanNumber(expenseForm.monthly_amount) <= 0) {
      alert('Monthly amount must be greater than 0.');
      return;
    }

    try {
      setSavingExpense(true);
      setAutoFillNotice(null);

      const payload = {
        name: expenseForm.name.trim(),
        category: expenseForm.category || 'Monthly Expense',
        monthly_amount: cleanNumber(expenseForm.monthly_amount),
        priority: Number(expenseForm.priority) || 1,
        due_day: expenseForm.due_day ? Number(expenseForm.due_day) : null,
        auto_allocate: Number(expenseForm.auto_allocate) ? 1 : 0,
        is_active: Number(expenseForm.is_active) ? 1 : 0,
        notes: expenseForm.notes || null,
      };

      let res;

      if (expenseForm.id) {
        res = await api.put(`/budget/expenses/${expenseForm.id}`, payload);
      } else {
        res = await api.post('/budget/expenses', payload);
      }

      const autoAllocation = res?.data?.autoAllocation || null;

      if (autoAllocation) {
        setAutoFillNotice(autoAllocation);
      }

      resetExpenseForm();
      await loadEverything();
    } catch (err) {
      console.error('[Budget] save expense error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to save expense.'
      );
    } finally {
      setSavingExpense(false);
    }
  };

  const saveGoal = async () => {
    if (!goalForm.name.trim()) {
      alert('Goal name is required.');
      return;
    }

    if (cleanNumber(goalForm.target_amount) <= 0) {
      alert('Goal target amount must be greater than 0.');
      return;
    }

    try {
      setSavingGoal(true);

      const payload = {
        name: goalForm.name.trim(),
        description: goalForm.description || null,
        target_amount: cleanNumber(goalForm.target_amount),
        current_amount: cleanNumber(goalForm.current_amount),
        contribution_percent: cleanNumber(goalForm.contribution_percent),
        priority: Number(goalForm.priority) || 1,
        status: goalForm.status || 'ACTIVE',
        starts_after_goal_id: goalForm.starts_after_goal_id || null,
      };

      if (goalForm.id) {
        await api.put(`/goals/${goalForm.id}`, payload);
      } else {
        await api.post('/goals', payload);
      }

      resetGoalForm();
      await loadEverything();
    } catch (err) {
      console.error('[Budget Goals] save goal error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to save goal.'
      );
    } finally {
      setSavingGoal(false);
    }
  };

  const deleteExpense = async (expense) => {
    if (
      !window.confirm(
        `Delete "${expense.name}"?\n\nThis also deletes allocation history tied to this expense.`
      )
    ) {
      return;
    }

    try {
      await api.delete(`/budget/expenses/${expense.id}`);
      await loadDashboard();
    } catch (err) {
      console.error('[Budget] delete expense error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to delete expense.'
      );
    }
  };

  const deleteGoal = async (goal) => {
    if (
      !window.confirm(
        `Delete goal "${goal.name}"?\n\nThis also deletes its contribution history.`
      )
    ) {
      return;
    }

    try {
      await api.delete(`/goals/${goal.id}`);
      await loadEverything();
    } catch (err) {
      console.error('[Budget Goals] delete goal error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to delete goal.'
      );
    }
  };

  const saveDailyProfit = async () => {
    if (cleanNumber(profitForm.net_amount) === 0) {
      alert('Enter a net amount.');
      return;
    }

    try {
      setSavingProfit(true);

      await api.post('/budget/daily-profit', {
        profit_date: profitForm.profit_date || todayDate(),
        platform: profitForm.platform || 'Shopify',
        gross_amount: cleanNumber(profitForm.net_amount),
        net_profit: cleanNumber(profitForm.net_amount),
        source: 'manual',
        notes: profitForm.notes || null,
      });

      setProfitForm({
        ...emptyProfitForm,
        profit_date: todayDate(),
        platform: 'Shopify',
      });

      await loadEverything();
    } catch (err) {
      console.error('[Budget] save net error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to save daily net.'
      );
    } finally {
      setSavingProfit(false);
    }
  };

  const deleteDailyProfit = async (item) => {
    if (!item.id) {
      alert('This is a live Shopify accounting row, not a manual saved entry. There is nothing to delete here.');
      return;
    }

    if (!window.confirm(`Delete ${item.platform} entry from ${getSafeDate(item.profit_date)}?`)) {
      return;
    }

    try {
      await api.delete(`/budget/daily-profit/${item.id}`);
      await loadEverything();
    } catch (err) {
      console.error('[Budget] delete profit error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to delete daily entry.'
      );
    }
  };

  const runAutoFillCheck = async () => {
    try {
      setAllocating(true);
      setAutoFillNotice(null);

      const res = await api.post('/budget/allocate', {
        allocation_date: dashboard?.today || todayDate(),
        platform: 'Shopify',
        notes: 'Manual auto-fill check from Budget page using net profit',
      });

      if (res?.data) {
        setAutoFillNotice(res.data);
      }

      await loadEverything();
    } catch (err) {
      console.error('[Budget] auto fill check error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to run auto fill check.'
      );
    } finally {
      setAllocating(false);
    }
  };

  const recordRecommendedGoalContribution = async () => {
    if (!activeGoal) {
      alert('No active goal found.');
      return;
    }

    const amount = customGoalContributionAmount
      ? cleanNumber(customGoalContributionAmount)
      : recommendedGoalContribution;

    if (amount <= 0) {
      alert('Contribution amount must be greater than 0.');
      return;
    }

    const confirmMessage =
      `Record goal contribution?\n\n` +
      `Goal: ${activeGoal.name}\n` +
      `Today Net: ${formatMoney(todayGoalNetProfit)}\n` +
      `Contribution: ${formatMoney(amount)}\n`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setRecordingGoal(true);

      await api.post('/goals/contribute', {
        goal_id: activeGoal.id,
        source: 'budget_page_shopify_profit',
        source_date: goalsDashboard?.today || todayDate(),
        net_profit: todayGoalNetProfit,
        contribution_percent: activeGoal.contribution_percent,
        contribution_amount: amount,
        notes: goalContributionNotes || 'Recorded from Budget page',
      });

      setCustomGoalContributionAmount('');
      setGoalContributionNotes('');

      await loadEverything();
    } catch (err) {
      console.error('[Budget Goals] contribution error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to record goal contribution.'
      );
    } finally {
      setRecordingGoal(false);
    }
  };

  const saveInventorySpend = async () => {
    if (cleanNumber(inventorySpendForm.amount) <= 0) {
      alert('Inventory spend amount must be greater than 0.');
      return;
    }

    try {
      setSavingSpend(true);

      const payload = {
        spend_date: inventorySpendForm.spend_date || todayDate(),
        amount: cleanNumber(inventorySpendForm.amount),
        vendor: inventorySpendForm.vendor || null,
        payment_method: inventorySpendForm.payment_method || null,
        reference: inventorySpendForm.reference || null,
        notes: inventorySpendForm.notes || null,
      };

      if (inventorySpendForm.id) {
        await api.put(`/budget/inventory-spend/${inventorySpendForm.id}`, payload);
      } else {
        await api.post('/budget/inventory-spend', payload);
      }

      resetInventorySpendForm();
      await loadDashboard();
    } catch (err) {
      console.error('[Budget] save inventory spend error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to save inventory spend.'
      );
    } finally {
      setSavingSpend(false);
    }
  };

  const deleteInventorySpend = async (item) => {
    if (!window.confirm(`Delete inventory spend of ${formatMoney(item.amount)}?`)) {
      return;
    }

    try {
      await api.delete(`/budget/inventory-spend/${item.id}`);
      await loadDashboard();
    } catch (err) {
      console.error('[Budget] delete inventory spend error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to delete inventory spend.'
      );
    }
  };

  const deleteAllocation = async (item) => {
    if (
      !window.confirm(
        `Delete allocation of ${formatMoney(item.allocation_amount)} to ${item.expense_name}?`
      )
    ) {
      return;
    }

    try {
      await api.delete(`/budget/allocations/${item.id}`);
      await loadDashboard();
    } catch (err) {
      console.error('[Budget] delete allocation error:', err.response?.data || err.message);

      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          'Failed to delete allocation.'
      );
    }
  };

  const getGoalProgressPercent = (goal) => {
    const target = cleanNumber(goal.target_amount);
    const current = cleanNumber(goal.current_amount);

    if (target <= 0) return 0;

    return Math.min(100, Math.max(0, (current / target) * 100));
  };

  const getGoalRemaining = (goal) => {
    return Math.max(
      0,
      cleanNumber(goal.target_amount) - cleanNumber(goal.current_amount)
    );
  };

  const GoalStatusBadge = ({ status }) => {
    const cleanStatus = String(status || '').toUpperCase();

    if (cleanStatus === 'COMPLETED') {
      return (
        <span
          className="badge"
          style={{
            background: 'rgba(34,197,94,0.18)',
            color: '#86efac',
            border: '1px solid rgba(34,197,94,0.28)',
          }}
        >
          Completed
        </span>
      );
    }

    if (cleanStatus === 'PAUSED') {
      return (
        <span
          className="badge"
          style={{
            background: 'rgba(148,163,184,0.16)',
            color: '#cbd5e1',
            border: '1px solid rgba(148,163,184,0.25)',
          }}
        >
          Paused
        </span>
      );
    }

    return (
      <span
        className="badge"
        style={{
          background: 'rgba(251,191,36,0.18)',
          color: '#fcd34d',
          border: '1px solid rgba(251,191,36,0.28)',
        }}
      >
        Active
      </span>
    );
  };

  const AutoFillNotice = ({ autoAllocation }) => {
    if (!autoAllocation) return null;

    const tone = getAutoAllocationTone(autoAllocation);

    return (
      <div
        className="mb-4 p-3"
        style={{
          borderRadius: 18,
          border: `1px solid ${tone.border}`,
          background: tone.background,
          color: tone.color,
        }}
      >
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <div style={{ fontWeight: 900, fontSize: 16 }}>
              {tone.icon} {tone.title}
            </div>

            <div style={{ color: '#cbd5e1', fontSize: 13, marginTop: 4 }}>
              {autoAllocation.message || 'Budget auto fill checked today’s available Shopify net profit.'}
            </div>
          </div>

          <button
            className="btn btn-sm btn-outline-light"
            onClick={() => setAutoFillNotice(null)}
          >
            Dismiss
          </button>
        </div>

        <div className="row g-2 mt-3">
          <div className="col-md-3">
            <MiniMetric label="Source Net" value={formatMoney(autoAllocation.source_net)} color="#22c55e" />
          </div>
          <div className="col-md-3">
            <MiniMetric label="Source COGS" value={formatMoney(autoAllocation.source_cogs)} color="#fbbf24" />
          </div>
          <div className="col-md-3">
            <MiniMetric label="Auto Filled" value={formatMoney(autoAllocation.allocated_total)} color="#22c55e" />
          </div>
          <div className="col-md-3">
            <MiniMetric label="Left From Today Net" value={formatMoney(autoAllocation.remaining_after_allocation)} color="#c4b5fd" />
          </div>
        </div>
      </div>
    );
  };

  const StatCard = ({
    label,
    value,
    subtext,
    accent = '#38bdf8',
    icon = '📊',
    valueColor,
    footer,
  }) => {
    return (
      <div className="col-md-6 col-xl-3 mb-3">
        <div
          className="h-100"
          style={{
            ...cardStyle,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle at top right, ${accent}33, transparent 36%)`,
              pointerEvents: 'none',
            }}
          />

          <div className="p-3 p-lg-4 position-relative">
            <div className="d-flex justify-content-between align-items-start gap-3">
              <div>
                <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  {label}
                </div>

                <div
                  className="mt-2"
                  style={{
                    fontSize: 30,
                    lineHeight: 1.05,
                    fontWeight: 900,
                    color: valueColor || '#f8fafc',
                  }}
                >
                  {value}
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 16,
                  background: `${accent}22`,
                  border: `1px solid ${accent}35`,
                  fontSize: 22,
                }}
              >
                {icon}
              </div>
            </div>

            {subtext && (
              <div className="mt-2" style={{ color: '#94a3b8', fontSize: 13 }}>
                {subtext}
              </div>
            )}

            {footer && <div className="mt-3">{footer}</div>}
          </div>
        </div>
      </div>
    );
  };

  const SectionHeader = ({ title, subtitle, action }) => {
    return (
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 850 }}>
            {title}
          </h4>
          {subtitle && <p className="mb-0" style={mutedStyle}>{subtitle}</p>}
        </div>
        {action}
      </div>
    );
  };

  const MiniMetric = ({ label, value, color = '#e5e7eb' }) => {
    return (
      <div
        style={{
          padding: '10px 12px',
          borderRadius: 16,
          background: 'rgba(2, 6, 23, 0.45)',
          border: '1px solid rgba(148, 163, 184, 0.12)',
        }}
      >
        <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 3 }}>{label}</div>
        <div style={{ color, fontWeight: 850 }}>{value}</div>
      </div>
    );
  };

  const ProgressBar = ({ value, height = 12 }) => {
    const clean = Math.min(100, Math.max(0, cleanNumber(value)));

    return (
      <div
        style={{
          width: '100%',
          height,
          borderRadius: 999,
          background: 'rgba(15, 23, 42, 0.82)',
          border: '1px solid rgba(148, 163, 184, 0.12)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${clean}%`,
            height: '100%',
            background: getProgressColor(clean),
            borderRadius: 999,
            transition: 'width 240ms ease',
          }}
        />
      </div>
    );
  };

  const ExpenseCard = ({ expense }) => {
    const progress = cleanNumber(expense.progress_percent);
    const remaining = cleanNumber(expense.remaining);

    return (
      <div className="col-md-6 col-xl-4 mb-3">
        <div className="h-100" style={cardStyle}>
          <div className="p-3 p-lg-4">
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h5 className="mb-1" style={{ color: '#f8fafc', fontWeight: 850 }}>
                  {expense.name}
                </h5>
                <div style={{ color: '#94a3b8', fontSize: 13 }}>
                  {expense.category || 'Monthly Expense'} · Priority #{expense.priority}
                </div>
              </div>

              {expense.is_filled ? (
                <span
                  className="badge"
                  style={{ background: 'rgba(34,197,94,0.18)', color: '#86efac', border: '1px solid rgba(34,197,94,0.28)' }}
                >
                  Filled
                </span>
              ) : (
                <span
                  className="badge"
                  style={{ background: 'rgba(245,158,11,0.18)', color: '#fcd34d', border: '1px solid rgba(245,158,11,0.28)' }}
                >
                  Needs {formatMoney(remaining)}
                </span>
              )}
            </div>

            <div className="row g-2 mb-3">
              <div className="col-6">
                <MiniMetric label="Filled" value={formatMoney(expense.filled)} color="#22c55e" />
              </div>
              <div className="col-6">
                <MiniMetric label="Target" value={formatMoney(expense.monthly_amount)} />
              </div>
            </div>

            <ProgressBar value={progress} />

            <div className="d-flex justify-content-between mt-2 mb-3" style={{ color: '#94a3b8', fontSize: 13 }}>
              <span>{formatPercent(progress)}</span>
              <span>Remaining: {formatMoney(remaining)}</span>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
              {expense.due_day && (
                <span style={pillStyle}>
                  Due day {expense.due_day}
                </span>
              )}

              {Number(expense.auto_allocate) ? (
                <span style={{ ...pillStyle, color: '#93c5fd', borderColor: 'rgba(59,130,246,0.35)' }}>
                  Auto Fill From Net
                </span>
              ) : (
                <span style={pillStyle}>
                  Manual Only
                </span>
              )}
            </div>

            {expense.notes && (
              <p className="mb-3" style={{ color: '#94a3b8', fontSize: 13 }}>
                {expense.notes}
              </p>
            )}

            <div className="d-flex flex-wrap gap-2">
              <button
                className="btn btn-sm btn-outline-info"
                onClick={() => startEditExpense(expense)}
              >
                Edit
              </button>

              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => deleteExpense(expense)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const GoalCard = ({ goal, highlight = false }) => {
    const progress = getGoalProgressPercent(goal);
    const remaining = getGoalRemaining(goal);

    return (
      <div className="col-md-6 col-xl-4 mb-3">
        <div
          className="h-100"
          style={{
            ...cardStyle,
            border: highlight
              ? '1px solid rgba(251,191,36,0.45)'
              : cardStyle.border,
            boxShadow: highlight
              ? '0 18px 55px rgba(251,191,36,0.10), 0 18px 45px rgba(0,0,0,0.28)'
              : cardStyle.boxShadow,
          }}
        >
          <div className="p-3 p-lg-4">
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h5 className="mb-1" style={{ color: '#f8fafc', fontWeight: 850 }}>
                  {goal.name}
                </h5>
                <div style={{ color: '#94a3b8', fontSize: 13 }}>
                  Priority #{goal.priority}
                  {goal.starts_after_goal_name ? ` · Starts after ${goal.starts_after_goal_name}` : ''}
                </div>
              </div>

              <GoalStatusBadge status={goal.status} />
            </div>

            {goal.description && (
              <p className="mb-3" style={{ color: '#94a3b8', fontSize: 13 }}>
                {goal.description}
              </p>
            )}

            <div className="row g-2 mb-3">
              <div className="col-6">
                <MiniMetric label="Saved" value={formatMoney(goal.current_amount)} color="#22c55e" />
              </div>
              <div className="col-6">
                <MiniMetric label="Target" value={formatMoney(goal.target_amount)} />
              </div>
            </div>

            <ProgressBar value={progress} />

            <div className="d-flex justify-content-between mt-2 mb-3" style={{ color: '#94a3b8', fontSize: 13 }}>
              <span>{formatPercent(progress)}</span>
              <span>Remaining: {formatMoney(remaining)}</span>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
              <span style={{ ...pillStyle, color: '#fcd34d', borderColor: 'rgba(251,191,36,0.35)' }}>
                Put Back {formatPercent(goal.contribution_percent)} of Net
              </span>

              {highlight && (
                <span style={{ ...pillStyle, color: '#86efac', borderColor: 'rgba(34,197,94,0.35)' }}>
                  Current Funding Goal
                </span>
              )}
            </div>

            <div className="d-flex flex-wrap gap-2">
              <button
                className="btn btn-sm btn-outline-info"
                onClick={() => startEditGoal(goal)}
              >
                Edit
              </button>

              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => deleteGoal(goal)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div className="container-fluid py-4" style={shellStyle}>
          <div className="p-4" style={cardStyle}>
            <div className="d-flex align-items-center gap-3">
              <div className="spinner-border text-info" role="status" />
              <div>
                <h4 className="mb-1" style={{ color: '#f8fafc' }}>
                  Loading Monthly Budget
                </h4>
                <p className="mb-0" style={mutedStyle}>
                  Pulling Shopify gross, COGS, net, expenses, inventory spend, goals, and auto-fill status.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <style>
        {`
          .budget-dark input,
          .budget-dark select,
          .budget-dark textarea {
            background-color: #020617 !important;
            color: #e5e7eb !important;
            border-color: rgba(148, 163, 184, 0.35) !important;
          }

          .budget-dark input::placeholder,
          .budget-dark textarea::placeholder {
            color: #64748b !important;
          }

          .budget-dark input:focus,
          .budget-dark select:focus,
          .budget-dark textarea:focus {
            box-shadow: 0 0 0 0.2rem rgba(56, 189, 248, 0.18) !important;
            border-color: rgba(56, 189, 248, 0.75) !important;
          }

          .budget-dark .table {
            --bs-table-bg: transparent;
            --bs-table-color: #e5e7eb;
            --bs-table-border-color: rgba(148, 163, 184, 0.12);
            color: #e5e7eb;
          }

          .budget-dark .table thead th {
            color: #cbd5e1;
            background: rgba(15, 23, 42, 0.94) !important;
            border-bottom: 1px solid rgba(148, 163, 184, 0.18);
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.6px;
          }

          .budget-dark .table tbody tr {
            border-color: rgba(148, 163, 184, 0.10);
          }

          .budget-dark .table tbody tr:hover {
            background: rgba(30, 41, 59, 0.48);
          }

          .budget-dark .form-label {
            color: #cbd5e1;
            font-size: 13px;
            font-weight: 700;
          }

          .budget-dark .btn {
            border-radius: 12px;
            font-weight: 700;
          }

          .budget-dark .alert {
            border-radius: 18px;
          }
        `}
      </style>

      <div className="container-fluid py-4 budget-dark" style={shellStyle}>
        <div className="p-4 p-lg-5 mb-4" style={heroStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-4">
            <div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span style={{ ...pillStyle, color: '#bfdbfe', borderColor: 'rgba(59,130,246,0.38)' }}>
                  Monthly Budget
                </span>
                <span style={{ ...pillStyle, color: '#bbf7d0', borderColor: 'rgba(34,197,94,0.38)' }}>
                  Expenses Use Net
                </span>
                <span style={{ ...pillStyle, color: '#fef3c7', borderColor: 'rgba(245,158,11,0.38)' }}>
                  COGS Builds Inventory Money
                </span>
                <span style={{ ...pillStyle, color: '#fde68a', borderColor: 'rgba(251,191,36,0.38)' }}>
                  Goals Sync With Goals Page
                </span>
              </div>

              <h1
                className="mb-3"
                style={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  letterSpacing: '-0.04em',
                  fontSize: 'clamp(2rem, 4vw, 4.35rem)',
                  lineHeight: 0.95,
                }}
              >
                Budget Control Center
              </h1>

              <p className="mb-0" style={{ color: '#cbd5e1', maxWidth: 900, fontSize: 17 }}>
                Expenses fill from Shopify net profit only. COGS builds your inventory spending balance,
                and goals can be created, edited, and funded from this page using the same data as your Goals page.
              </p>
            </div>

            <div
              className="p-3"
              style={{
                ...cardSoftStyle,
                minWidth: 290,
              }}
            >
              <div className="mb-3">
                <label className="form-label">Budget Month</label>
                <input
                  type="month"
                  className="form-control"
                  style={inputStyle}
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                />
              </div>

              <div className="d-grid gap-2">
                <button className="btn btn-info" onClick={loadEverything}>
                  Refresh Dashboard
                </button>

                <button className="btn btn-success" onClick={startCreateExpense}>
                  + Add Monthly Expense
                </button>

                <button className="btn btn-warning" onClick={startCreateGoal}>
                  + Add Goal
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div
            className="alert mb-4"
            style={{
              background: 'rgba(127,29,29,0.70)',
              border: '1px solid rgba(248,113,113,0.35)',
              color: '#fee2e2',
            }}
          >
            <strong>Budget could not load.</strong>
            <div>{error}</div>
            <button className="btn btn-sm btn-light mt-2" onClick={loadDashboard}>
              Retry
            </button>
          </div>
        )}

        {goalsError && (
          <div
            className="alert mb-4"
            style={{
              background: 'rgba(127,29,29,0.70)',
              border: '1px solid rgba(248,113,113,0.35)',
              color: '#fee2e2',
            }}
          >
            <strong>Goals could not load.</strong>
            <div>{goalsError}</div>
            <button className="btn btn-sm btn-light mt-2" onClick={loadGoalsDashboard}>
              Retry Goals
            </button>
          </div>
        )}

        <AutoFillNotice autoAllocation={autoFillNotice} />

        <div className="mb-4" style={cardStyle}>
          <div className="p-4">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
              <div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <h3 className="mb-0" style={{ color: '#f8fafc', fontWeight: 900 }}>
                    {moneyStatus.icon} Budget Status
                  </h3>

                  <span
                    style={{
                      border: `1px solid ${moneyStatus.border}`,
                      background: moneyStatus.bg,
                      color: moneyStatus.tone,
                      borderRadius: 999,
                      padding: '7px 12px',
                      fontSize: 12,
                      fontWeight: 850,
                    }}
                  >
                    {moneyStatus.label}
                  </span>
                </div>

                <p className="mb-0" style={mutedStyle}>
                  {moneyStatus.message}
                </p>
              </div>

              <div className="text-end">
                <div style={{ color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Current Month
                </div>
                <div style={{ color: '#f8fafc', fontSize: 26, fontWeight: 900 }}>
                  {selectedMonth}
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <MiniMetric label="Expenses Filled From Net" value={formatMoney(expensesFilled)} color="#22c55e" />
              </div>
              <div className="col-md-3">
                <MiniMetric label="Expense Target" value={formatMoney(expenseTarget)} />
              </div>
              <div className="col-md-3">
                <MiniMetric label="Expenses Remaining" value={formatMoney(expensesRemaining)} color={expensesRemaining > 0 ? '#f59e0b' : '#22c55e'} />
              </div>
              <div className="col-md-3">
                <MiniMetric label="Progress" value={formatPercent(totalExpenseProgress)} color="#38bdf8" />
              </div>
            </div>

            <ProgressBar value={totalExpenseProgress} height={16} />
          </div>
        </div>

        <SectionHeader
          title="Shopify Money Snapshot"
          subtitle="Expenses use net. Goals use their put-back rules. Inventory spending balance uses COGS."
        />

        <div className="row mb-4">
          <StatCard
            label="Monthly Gross"
            value={formatMoney(totalGross)}
            subtext="Item sales + buyer shipping"
            accent="#38bdf8"
            icon="💰"
          />

          <StatCard
            label="Monthly COGS"
            value={formatMoney(totalCogs)}
            subtext="Builds inventory spending money"
            accent="#f59e0b"
            icon="📦"
            valueColor="#fbbf24"
          />

          <StatCard
            label="Monthly Net"
            value={formatMoney(totalNet)}
            subtext="Used to fill expenses and goals"
            accent="#22c55e"
            icon="✅"
            valueColor={getMoneyColor(totalNet)}
          />

          <StatCard
            label="To Spend On Inventory"
            value={formatMoney(toSpendOnInventory)}
            subtext={`${formatMoney(inventoryBudgetFromCogs)} COGS - ${formatMoney(inventoryBudgetUsed)} spent`}
            accent="#a855f7"
            icon="🛒"
            valueColor={getMoneyColor(toSpendOnInventory)}
          />
        </div>

        <div className="row mb-4">
          <div className="col-xl-7 mb-3">
            <div className="h-100 p-4" style={cardStyle}>
              <SectionHeader
                title="Money Flow"
                subtitle="Net fills expenses and goals. COGS builds inventory buying power."
              />

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={mutedStyle}>COGS Built For Inventory</span>
                  <span style={{ color: '#fbbf24', fontWeight: 800 }}>
                    {formatMoney(inventoryBudgetFromCogs)} · {formatPercent(cogsPercent)} of gross
                  </span>
                </div>
                <ProgressBar value={cogsPercent} />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={mutedStyle}>Net Allocated to Expenses</span>
                  <span style={{ color: '#93c5fd', fontWeight: 800 }}>
                    {formatMoney(totalAllocatedToExpenses)} · {formatPercent(expensePercentOfNet)} of net
                  </span>
                </div>
                <ProgressBar value={expensePercentOfNet} />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={mutedStyle}>Goal Progress</span>
                  <span style={{ color: '#fcd34d', fontWeight: 800 }}>
                    {formatMoney(totalGoalSaved)} saved · {formatPercent(totalGoalProgress)}
                  </span>
                </div>
                <ProgressBar value={totalGoalProgress} />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={mutedStyle}>Inventory Spend Used From COGS</span>
                  <span style={{ color: '#c4b5fd', fontWeight: 800 }}>
                    {formatMoney(inventoryBudgetUsed)} · {formatPercent(inventorySpendPercentOfCogs)} of COGS
                  </span>
                </div>
                <ProgressBar value={inventorySpendPercentOfCogs} />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={mutedStyle}>COGS Remaining To Spend On Inventory</span>
                  <span style={{ color: getMoneyColor(toSpendOnInventory), fontWeight: 800 }}>
                    {formatMoney(toSpendOnInventory)} · {formatPercent(inventoryRemainingPercent)}
                  </span>
                </div>
                <ProgressBar value={inventoryRemainingPercent} />
              </div>

              <hr style={dividerStyle} />

              <div className="row g-2">
                <div className="col-md-4">
                  <MiniMetric label="Item Sales" value={formatMoney(totalSales)} />
                </div>
                <div className="col-md-4">
                  <MiniMetric label="Buyer Shipping" value={formatMoney(totalBuyerShippingPaid)} color="#38bdf8" />
                </div>
                <div className="col-md-4">
                  <MiniMetric
                    label="Fees / Shipping / Credit"
                    value={formatMoney(totalPlatformFees + totalShippingCost + totalStoreCreditUsed)}
                    color="#fb7185"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-5 mb-3">
            <div className="h-100 p-4" style={cardStyle}>
              <SectionHeader
                title="Today’s Auto Fill"
                subtitle="Expenses auto-fill from today’s Shopify net. Goals can be funded after expenses."
              />

              <div className="row g-2 mb-3">
                <div className="col-6">
                  <MiniMetric label="Today Gross" value={formatMoney(todayGross)} color="#38bdf8" />
                </div>
                <div className="col-6">
                  <MiniMetric label="Today Net For Expenses" value={formatMoney(todayNet)} color={getMoneyColor(todayNet)} />
                </div>
                <div className="col-6">
                  <MiniMetric label="Today COGS For Inventory" value={formatMoney(todayCogs)} color="#fbbf24" />
                </div>
                <div className="col-6">
                  <MiniMetric label="Goal Put-Back Ready" value={formatMoney(recommendedGoalContribution)} color="#fcd34d" />
                </div>
              </div>

              <button
                className="btn btn-warning w-100"
                onClick={runAutoFillCheck}
                disabled={allocating || todayNet <= 0 || expenses.length === 0}
              >
                {allocating ? 'Checking Auto Fill...' : 'Run Net Auto Fill Check'}
              </button>

              <p className="mb-0 mt-3" style={{ color: '#94a3b8', fontSize: 13 }}>
                This is duplicate-safe for expenses. Goal funding below records against the same Goals page records.
              </p>
            </div>
          </div>
        </div>

        <SectionHeader
          title="Goals Funding"
          subtitle="Create, edit, and fund goals here. These are the same goals shown on your Goals page."
          action={
            <div className="d-flex flex-wrap gap-2">
              <button className="btn btn-sm btn-outline-info" onClick={loadGoalsDashboard} disabled={goalsLoading}>
                {goalsLoading ? 'Refreshing...' : 'Refresh Goals'}
              </button>
              <button className="btn btn-sm btn-warning" onClick={startCreateGoal}>
                + Add Goal
              </button>
            </div>
          }
        />

        <div className="row mb-4">
          <StatCard
            label="Goal Target Total"
            value={formatMoney(totalGoalTarget)}
            subtext={`${goals.length} total goals`}
            accent="#f59e0b"
            icon="🎯"
            valueColor="#fcd34d"
          />

          <StatCard
            label="Saved Toward Goals"
            value={formatMoney(totalGoalSaved)}
            subtext={`${formatPercent(totalGoalProgress)} complete`}
            accent="#22c55e"
            icon="🏦"
            valueColor="#86efac"
          />

          <StatCard
            label="Goal Remaining"
            value={formatMoney(totalGoalRemaining)}
            subtext="Total still needed"
            accent="#38bdf8"
            icon="📈"
          />

          <StatCard
            label="Recommended Today"
            value={formatMoney(recommendedGoalContribution)}
            subtext={activeGoal ? `For ${activeGoal.name}` : 'No active funding goal'}
            accent="#f59e0b"
            icon="⚡"
            valueColor="#fcd34d"
          />
        </div>

        {showGoalForm && (
          <div ref={goalFormRef} className="p-4 mb-4" style={cardStyle}>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                  {goalForm.id ? 'Edit Goal' : 'Create Goal'}
                </h4>
                <p className="mb-0" style={mutedStyle}>
                  Saving here updates the same goal record used by your Goals page.
                </p>
              </div>

              <button className="btn btn-sm btn-outline-light" onClick={resetGoalForm}>
                Cancel
              </button>
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Goal Name</label>
                <input
                  className="form-control"
                  value={goalForm.name}
                  onChange={(e) => updateGoalForm('name', e.target.value)}
                  placeholder="Emergency Fund, Garage, Security System..."
                />
              </div>

              <div className="col-md-2 mb-3">
                <label className="form-label">Target Amount</label>
                <input
                  className="form-control"
                  value={goalForm.target_amount}
                  onChange={(e) => updateGoalForm('target_amount', e.target.value)}
                  placeholder="10000"
                />
              </div>

              <div className="col-md-2 mb-3">
                <label className="form-label">Current Amount</label>
                <input
                  className="form-control"
                  value={goalForm.current_amount}
                  onChange={(e) => updateGoalForm('current_amount', e.target.value)}
                  placeholder="0"
                />
              </div>

              <div className="col-md-2 mb-3">
                <label className="form-label">Put-Back %</label>
                <input
                  className="form-control"
                  value={goalForm.contribution_percent}
                  onChange={(e) => updateGoalForm('contribution_percent', e.target.value)}
                  placeholder="80"
                />
              </div>

              <div className="col-md-2 mb-3">
                <label className="form-label">Priority</label>
                <input
                  className="form-control"
                  value={goalForm.priority}
                  onChange={(e) => updateGoalForm('priority', e.target.value)}
                  placeholder="1"
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={goalForm.status}
                  onChange={(e) => updateGoalForm('status', e.target.value)}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="PAUSED">Paused</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Starts After</label>
                <select
                  className="form-select"
                  value={goalForm.starts_after_goal_id}
                  onChange={(e) => updateGoalForm('starts_after_goal_id', e.target.value)}
                >
                  <option value="">No dependency</option>

                  {goals
                    .filter((goal) => String(goal.id) !== String(goalForm.id))
                    .map((goal) => (
                      <option key={goal.id} value={goal.id}>
                        {goal.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Description</label>
                <input
                  className="form-control"
                  value={goalForm.description}
                  onChange={(e) => updateGoalForm('description', e.target.value)}
                  placeholder="Example: Build emergency fund before garage upgrades."
                />
              </div>
            </div>

            <button className="btn btn-warning" onClick={saveGoal} disabled={savingGoal}>
              {savingGoal
                ? 'Saving Goal...'
                : goalForm.id
                  ? 'Save Goal'
                  : 'Create Goal'}
            </button>
          </div>
        )}

        <div className="row mb-4">
          <div className="col-xl-5 mb-3">
            <div className="p-4 h-100" style={cardStyle}>
              <SectionHeader
                title="Daily Goal Put-Back"
                subtitle="Uses your active goal’s put-back percent and today’s Shopify net."
              />

              {activeGoal ? (
                <>
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <MiniMetric label="Today Shopify Net" value={formatMoney(todayGoalNetProfit)} color={getMoneyColor(todayGoalNetProfit)} />
                    </div>
                    <div className="col-6">
                      <MiniMetric label="Recommended Put-Back" value={formatMoney(recommendedGoalContribution)} color="#fcd34d" />
                    </div>
                    <div className="col-6">
                      <MiniMetric label="Recorded Today" value={formatMoney(contributedTodayToGoals)} color="#86efac" />
                    </div>
                    <div className="col-6">
                      <MiniMetric label="After Put-Back" value={formatMoney(remainingAfterGoalRecommended)} />
                    </div>
                  </div>

                  <div
                    className="p-3 mb-3"
                    style={{
                      borderRadius: 16,
                      background: 'rgba(251,191,36,0.10)',
                      border: '1px solid rgba(251,191,36,0.20)',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <div style={{ color: '#f8fafc', fontWeight: 900 }}>
                          Funding: {activeGoal.name}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: 13 }}>
                          Put-back rule: {formatPercent(activeGoal.contribution_percent)} of net
                        </div>
                      </div>
                      <GoalStatusBadge status={activeGoal.status} />
                    </div>
                  </div>

                  <div className="row align-items-end">
                    <div className="col-md-5 mb-3">
                      <label className="form-label">Custom Contribution</label>
                      <input
                        className="form-control"
                        value={customGoalContributionAmount}
                        onChange={(e) => setCustomGoalContributionAmount(e.target.value)}
                        placeholder={recommendedGoalContribution.toFixed(2)}
                      />
                    </div>

                    <div className="col-md-7 mb-3">
                      <label className="form-label">Notes</label>
                      <input
                        className="form-control"
                        value={goalContributionNotes}
                        onChange={(e) => setGoalContributionNotes(e.target.value)}
                        placeholder="Budget page goal put-back"
                      />
                    </div>

                    <div className="col-12">
                      <button
                        className="btn btn-warning w-100"
                        onClick={recordRecommendedGoalContribution}
                        disabled={recordingGoal || recommendedGoalContribution <= 0}
                      >
                        {recordingGoal ? 'Recording...' : 'Record Goal Put-Back'}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div
                  className="p-3"
                  style={{
                    borderRadius: 16,
                    background: 'rgba(148,163,184,0.10)',
                    border: '1px solid rgba(148,163,184,0.18)',
                    color: '#cbd5e1',
                  }}
                >
                  No active goal is ready for funding. Create a goal or complete the goal it depends on.
                </div>
              )}
            </div>
          </div>

          <div className="col-xl-7 mb-3">
            <div className="p-4 h-100" style={cardStyle}>
              <SectionHeader
                title="Active Goal Queue"
                subtitle="Goals fill by active status, priority, and dependency rules."
              />

              {activeGoal && (
                <div className="row">
                  <GoalCard goal={activeGoal} highlight />
                </div>
              )}

              {!activeGoal && activeGoals.length === 0 && (
                <div style={mutedStyle}>No active goals yet.</div>
              )}

              {activeGoals.length > 0 && (
                <>
                  <hr style={dividerStyle} />
                  <div className="row">
                    {activeGoals
                      .filter((goal) => !activeGoal || Number(goal.id) !== Number(activeGoal.id))
                      .slice(0, 6)
                      .map((goal) => (
                        <GoalCard key={goal.id} goal={goal} />
                      ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {(pausedGoals.length > 0 || completedGoals.length > 0 || recentGoalContributions.length > 0) && (
          <div className="row mb-4">
            <div className="col-xl-6 mb-3">
              <div className="p-4 h-100" style={cardStyle}>
                <SectionHeader
                  title="Recent Goal Contributions"
                  subtitle="Latest goal funding entries from the shared goal system."
                />

                {recentGoalContributions.length === 0 ? (
                  <div style={mutedStyle}>No goal contributions recorded yet.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle" style={tableStyle}>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Goal</th>
                          <th>Source</th>
                          <th className="text-end">Amount</th>
                        </tr>
                      </thead>

                      <tbody>
                        {recentGoalContributions.slice(0, 12).map((item) => (
                          <tr key={item.id}>
                            <td>{getSafeDate(item.source_date || item.created_at)}</td>
                            <td>
                              <strong style={{ color: '#f8fafc' }}>{item.goal_name}</strong>
                              {item.notes && <div style={{ ...mutedStyle, fontSize: 12 }}>{item.notes}</div>}
                            </td>
                            <td>
                              <span style={pillStyle}>{item.source || '—'}</span>
                            </td>
                            <td className="text-end">
                              <strong style={{ color: '#fcd34d' }}>
                                {formatMoney(item.contribution_amount)}
                              </strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="col-xl-6 mb-3">
              <div className="p-4 h-100" style={cardStyle}>
                <SectionHeader
                  title="Paused / Completed Goals"
                  subtitle="Quick view of goals not currently taking funding."
                />

                {pausedGoals.length === 0 && completedGoals.length === 0 ? (
                  <div style={mutedStyle}>No paused or completed goals yet.</div>
                ) : (
                  <div className="row">
                    {[...pausedGoals, ...completedGoals].slice(0, 6).map((goal) => (
                      <GoalCard key={goal.id} goal={goal} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <SectionHeader
          title="Inventory Spending Card"
          subtitle="This is based on monthly COGS minus inventory spend."
        />

        <div className="row mb-4">
          <StatCard
            label="COGS Built This Month"
            value={formatMoney(inventoryBudgetFromCogs)}
            subtext="Total COGS from Shopify accounting"
            accent="#f59e0b"
            icon="📦"
            valueColor="#fbbf24"
          />

          <StatCard
            label="Inventory Already Spent"
            value={formatMoney(inventoryBudgetUsed)}
            subtext="Manual inventory purchases recorded"
            accent="#a855f7"
            icon="🛍️"
            valueColor="#c4b5fd"
          />

          <StatCard
            label="To Spend On Inventory"
            value={formatMoney(toSpendOnInventory)}
            subtext="COGS left after inventory spend"
            accent="#22c55e"
            icon="🛒"
            valueColor={getMoneyColor(toSpendOnInventory)}
          />

          <StatCard
            label="Inventory Remaining"
            value={formatPercent(inventoryRemainingPercent)}
            subtext="Percent of COGS still available"
            accent="#38bdf8"
            icon="📊"
          />
        </div>

        <SectionHeader
          title="Budget Breakdown"
          subtitle="What is targeted, funded, spent, and still safe."
        />

        <div className="row mb-4">
          <StatCard
            label="Expense Target"
            value={formatMoney(expenseTarget)}
            subtext="Total active monthly bills"
            accent="#64748b"
            icon="🧾"
          />

          <StatCard
            label="Allocated To Expenses"
            value={formatMoney(totalAllocatedToExpenses)}
            subtext="Net auto-filled or manually allocated"
            accent="#3b82f6"
            icon="🏦"
          />

          <StatCard
            label="Inventory Spent"
            value={formatMoney(totalInventorySpend)}
            subtext="Subtracts from COGS inventory money"
            accent="#a855f7"
            icon="🛍️"
          />

          <StatCard
            label="Safe Remaining Gross"
            value={formatMoney(safeRemainingGross)}
            subtext="Gross minus expenses and inventory spend"
            accent="#22c55e"
            icon="🔒"
            valueColor={getMoneyColor(safeRemainingGross)}
          />
        </div>

        <div className="row mb-4">
          <StatCard
            label="Net After Expenses"
            value={formatMoney(safeRemainingNet)}
            subtext="Net minus expense allocations"
            accent="#22c55e"
            icon="🟢"
            valueColor={getMoneyColor(safeRemainingNet)}
          />

          <StatCard
            label="Platform Fees"
            value={formatMoney(totalPlatformFees)}
            subtext="Fees from order items"
            accent="#ef4444"
            icon="💳"
            valueColor="#fca5a5"
          />

          <StatCard
            label="Shipping Cost"
            value={formatMoney(totalShippingCost)}
            subtext="Allocated shipping cost"
            accent="#f97316"
            icon="🚚"
            valueColor="#fdba74"
          />

          <StatCard
            label="Store Credit"
            value={formatMoney(totalStoreCreditUsed)}
            subtext="Store credit used"
            accent="#eab308"
            icon="🎟️"
            valueColor="#fde047"
          />
        </div>

        {showExpenseForm && (
          <div ref={expenseFormRef} className="p-4 mb-4" style={cardStyle}>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                  {expenseForm.id ? 'Edit Monthly Expense' : 'Add Monthly Expense'}
                </h4>
                <p className="mb-0" style={mutedStyle}>
                  When saved, auto-fill checks today’s available Shopify net and fills expenses by priority.
                </p>
              </div>

              <button className="btn btn-sm btn-outline-light" onClick={resetExpenseForm}>
                Cancel
              </button>
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Expense Name</label>
                <input
                  className="form-control"
                  value={expenseForm.name}
                  onChange={(e) => updateExpenseForm('name', e.target.value)}
                  placeholder="Rent, Internet, Taxes, Software..."
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Category</label>
                <input
                  className="form-control"
                  value={expenseForm.category}
                  onChange={(e) => updateExpenseForm('category', e.target.value)}
                  placeholder="Fixed, Software, Tax..."
                />
              </div>

              <div className="col-md-2 mb-3">
                <label className="form-label">Monthly Amount</label>
                <input
                  className="form-control"
                  value={expenseForm.monthly_amount}
                  onChange={(e) => updateExpenseForm('monthly_amount', e.target.value)}
                  placeholder="1500"
                />
              </div>

              <div className="col-md-1 mb-3">
                <label className="form-label">Priority</label>
                <input
                  className="form-control"
                  value={expenseForm.priority}
                  onChange={(e) => updateExpenseForm('priority', e.target.value)}
                  placeholder="1"
                />
              </div>

              <div className="col-md-2 mb-3">
                <label className="form-label">Due Day</label>
                <input
                  className="form-control"
                  value={expenseForm.due_day}
                  onChange={(e) => updateExpenseForm('due_day', e.target.value)}
                  placeholder="15"
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Auto Fill</label>
                <select
                  className="form-select"
                  value={expenseForm.auto_allocate}
                  onChange={(e) => updateExpenseForm('auto_allocate', e.target.value)}
                >
                  <option value={1}>Yes - auto fill from net by priority</option>
                  <option value={0}>No - manual only</option>
                </select>
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={expenseForm.is_active}
                  onChange={(e) => updateExpenseForm('is_active', e.target.value)}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Notes</label>
                <input
                  className="form-control"
                  value={expenseForm.notes}
                  onChange={(e) => updateExpenseForm('notes', e.target.value)}
                  placeholder="Optional note"
                />
              </div>
            </div>

            <button className="btn btn-info" onClick={saveExpense} disabled={savingExpense}>
              {savingExpense
                ? 'Saving + Auto Filling...'
                : expenseForm.id
                  ? 'Save Expense + Net Auto Fill'
                  : 'Create Expense + Net Auto Fill'}
            </button>
          </div>
        )}

        <div className="row mb-4">
          <div className="col-xl-6 mb-3">
            <div className="p-4 h-100" style={cardStyle}>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                    Manual Net Entry
                  </h4>
                  <p className="mb-0" style={mutedStyle}>
                    Shopify is pulled live. Use this for future platforms or manual net adjustments.
                  </p>
                </div>

                <span style={{ ...pillStyle, color: '#93c5fd', borderColor: 'rgba(59,130,246,0.35)' }}>
                  Optional
                </span>
              </div>

              <div className="row align-items-end">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={profitForm.profit_date}
                    onChange={(e) => updateProfitForm('profit_date', e.target.value)}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Platform</label>
                  <select
                    className="form-select"
                    value={profitForm.platform}
                    onChange={(e) => updateProfitForm('platform', e.target.value)}
                  >
                    <option value="Shopify">Shopify</option>
                    <option value="eBay">eBay</option>
                    <option value="Walmart">Walmart</option>
                    <option value="TikTok">TikTok</option>
                    <option value="Mercari">Mercari</option>
                    <option value="Poshmark">Poshmark</option>
                    <option value="Manual">Manual</option>
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Net Amount</label>
                  <input
                    className="form-control"
                    value={profitForm.net_amount}
                    onChange={(e) => updateProfitForm('net_amount', e.target.value)}
                    placeholder="500"
                  />
                </div>

                <div className="col-md-8 mb-3">
                  <label className="form-label">Notes</label>
                  <input
                    className="form-control"
                    value={profitForm.notes}
                    onChange={(e) => updateProfitForm('notes', e.target.value)}
                    placeholder="Optional note"
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <button
                    className="btn btn-info w-100"
                    onClick={saveDailyProfit}
                    disabled={savingProfit}
                  >
                    {savingProfit ? 'Saving...' : 'Save Entry'}
                  </button>
                </div>
              </div>

              <div
                className="p-3"
                style={{
                  borderRadius: 16,
                  background: 'rgba(59,130,246,0.10)',
                  border: '1px solid rgba(59,130,246,0.20)',
                  color: '#bfdbfe',
                  fontSize: 13,
                }}
              >
                <strong>Note:</strong> For Shopify, live accounting is the main source. Manual entries are
                mainly for other platforms until they are connected.
              </div>
            </div>
          </div>

          <div className="col-xl-6 mb-3">
            <div className="p-4 h-100" style={cardStyle}>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                    {inventorySpendForm.id ? 'Edit Inventory Spend' : 'Inventory Spend'}
                  </h4>
                  <p className="mb-0" style={mutedStyle}>
                    This subtracts from your COGS-based inventory spending balance.
                  </p>
                </div>

                {inventorySpendForm.id && (
                  <button className="btn btn-sm btn-outline-light" onClick={resetInventorySpendForm}>
                    Cancel Edit
                  </button>
                )}
              </div>

              <div className="row align-items-end">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={inventorySpendForm.spend_date}
                    onChange={(e) => updateInventorySpendForm('spend_date', e.target.value)}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Amount</label>
                  <input
                    className="form-control"
                    value={inventorySpendForm.amount}
                    onChange={(e) => updateInventorySpendForm('amount', e.target.value)}
                    placeholder="300"
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Vendor</label>
                  <input
                    className="form-control"
                    value={inventorySpendForm.vendor}
                    onChange={(e) => updateInventorySpendForm('vendor', e.target.value)}
                    placeholder="Distributor / Seller"
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Payment Method</label>
                  <input
                    className="form-control"
                    value={inventorySpendForm.payment_method}
                    onChange={(e) => updateInventorySpendForm('payment_method', e.target.value)}
                    placeholder="Card, bank, cash..."
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Reference</label>
                  <input
                    className="form-control"
                    value={inventorySpendForm.reference}
                    onChange={(e) => updateInventorySpendForm('reference', e.target.value)}
                    placeholder="Order # / invoice"
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Notes</label>
                  <input
                    className="form-control"
                    value={inventorySpendForm.notes}
                    onChange={(e) => updateInventorySpendForm('notes', e.target.value)}
                    placeholder="Optional"
                  />
                </div>

                <div className="col-12">
                  <button className="btn btn-success w-100" onClick={saveInventorySpend} disabled={savingSpend}>
                    {savingSpend
                      ? 'Saving...'
                      : inventorySpendForm.id
                        ? 'Save Inventory Spend'
                        : 'Record Inventory Spend'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <SectionHeader
          title="Monthly Expenses"
          subtitle="Auto-fill expenses are filled from net by priority."
          action={
            <button className="btn btn-sm btn-success" onClick={startCreateExpense}>
              + Add Expense
            </button>
          }
        />

        <div className="mb-4">
          {expenses.length === 0 ? (
            <div
              className="p-4"
              style={{
                ...cardStyle,
                color: '#cbd5e1',
              }}
            >
              No monthly expenses yet. Add rent, software, taxes, shipping supplies, subscriptions,
              or anything you want daily net profit to fill.
            </div>
          ) : (
            <div className="row">
              {expenses.map((expense) => (
                <ExpenseCard key={expense.id} expense={expense} />
              ))}
            </div>
          )}
        </div>

        <div className="row mb-4">
          <div className="col-xl-6 mb-3">
            <div className="p-4 h-100" style={cardStyle}>
              <SectionHeader
                title="Recent Expense Allocations"
                subtitle="Net money auto-filled or manually applied toward monthly expenses."
              />

              {allocations.length === 0 ? (
                <div style={mutedStyle}>No allocations recorded for this month.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle" style={tableStyle}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Expense</th>
                        <th>Platform</th>
                        <th className="text-end">Amount</th>
                        <th></th>
                      </tr>
                    </thead>

                    <tbody>
                      {allocations.slice(0, 20).map((item) => (
                        <tr key={item.id}>
                          <td>{getSafeDate(item.allocation_date)}</td>
                          <td>
                            <strong style={{ color: '#f8fafc' }}>{item.expense_name}</strong>
                            {item.notes && <div style={{ ...mutedStyle, fontSize: 12 }}>{item.notes}</div>}
                          </td>
                          <td>
                            <span style={pillStyle}>{item.platform}</span>
                          </td>
                          <td className="text-end">
                            <strong style={{ color: '#22c55e' }}>{formatMoney(item.allocation_amount)}</strong>
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteAllocation(item)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="col-xl-6 mb-3">
            <div className="p-4 h-100" style={cardStyle}>
              <SectionHeader
                title="Inventory Spend Log"
                subtitle="Inventory purchases recorded against your COGS spending balance."
              />

              {inventorySpend.length === 0 ? (
                <div style={mutedStyle}>No inventory spending recorded for this month.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle" style={tableStyle}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Vendor</th>
                        <th className="text-end">Amount</th>
                        <th>Reference</th>
                        <th></th>
                      </tr>
                    </thead>

                    <tbody>
                      {inventorySpend.slice(0, 20).map((item) => (
                        <tr key={item.id}>
                          <td>{getSafeDate(item.spend_date)}</td>
                          <td>
                            <strong style={{ color: '#f8fafc' }}>{item.vendor || '—'}</strong>
                            {item.notes && <div style={{ ...mutedStyle, fontSize: 12 }}>{item.notes}</div>}
                          </td>
                          <td className="text-end">
                            <strong style={{ color: '#c4b5fd' }}>{formatMoney(item.amount)}</strong>
                          </td>
                          <td>{item.reference || '—'}</td>
                          <td className="text-end">
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-info"
                                onClick={() => startEditInventorySpend(item)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => deleteInventorySpend(item)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 mb-4" style={cardStyle}>
          <SectionHeader
            title="Daily Money Breakdown"
            subtitle="Shopify rows are live from accounting. Manual rows can be deleted."
          />

          {dailyProfits.length === 0 ? (
            <div style={mutedStyle}>No daily money entries found for this month.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle" style={tableStyle}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Platform</th>
                    <th>Source</th>
                    <th className="text-end">Gross</th>
                    <th className="text-end">COGS</th>
                    <th className="text-end">Net</th>
                    <th className="text-end">Qty</th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {dailyProfits.map((item, index) => {
                    const rowGross = cleanNumber(item.gross_amount ?? item.net_profit);
                    const rowCogs = cleanNumber(item.cogs_amount);
                    const rowNet = cleanNumber(item.net_amount ?? item.net_profit);
                    const isLive = String(item.source || '').includes('live');

                    return (
                      <tr key={item.id || `${item.platform}-${item.profit_date}-${index}`}>
                        <td>{getSafeDate(item.profit_date)}</td>
                        <td>
                          <span style={pillStyle}>
                            {item.platform || '—'}
                          </span>
                        </td>
                        <td>
                          {isLive ? (
                            <span
                              style={{
                                ...pillStyle,
                                color: '#86efac',
                                borderColor: 'rgba(34,197,94,0.35)',
                                background: 'rgba(34,197,94,0.10)',
                              }}
                            >
                              Live Accounting
                            </span>
                          ) : (
                            <span style={pillStyle}>{item.source || 'Manual'}</span>
                          )}
                        </td>
                        <td className="text-end">
                          <strong style={{ color: '#38bdf8' }}>{formatMoney(rowGross)}</strong>
                        </td>
                        <td className="text-end">
                          <span style={{ color: '#fbbf24' }}>{formatMoney(rowCogs)}</span>
                        </td>
                        <td className="text-end">
                          <strong style={{ color: getMoneyColor(rowNet) }}>
                            {formatMoney(rowNet)}
                          </strong>
                        </td>
                        <td className="text-end">{cleanNumber(item.total_quantity)}</td>
                        <td>{item.notes || '—'}</td>
                        <td className="text-end">
                          {item.id ? (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteDailyProfit(item)}
                            >
                              Delete
                            </button>
                          ) : (
                            <span style={{ color: '#64748b', fontSize: 12 }}>Live</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}